import React, { createContext, useState, useEffect, useCallback } from 'react';
import Constantes from '../constantes';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [userData, setUserData] = useState(null);

    const validateSession = useCallback(async () => {
        try {
            const localStorageLogin = localStorage.getItem('Login');
            console.log('validateSession - localStorage Login:', localStorageLogin);
            
            if (localStorageLogin !== 'true') {
                console.log('validateSession - No authenticated in localStorage');
                setIsAuthenticated(false);
                setUserData(null);
                setIsLoading(false);
                return;
            }

            // Si localStorage dice que está autenticado, validar con el backend
            console.log('validateSession - Fetching user data from backend...');
            const response = await fetch(Constantes.RUTA_API + '/usuarios/datosUsuario', {
                method: 'GET',
                credentials: 'include'
            });

            console.log('validateSession - Response status:', response.status);

            if (!response.ok) {
                // Si la respuesta no es OK (401, 403, etc), la sesión ha expirado
                console.log('validateSession - Response not ok, sesión expirada');
                localStorage.setItem('Login', 'false');
                setIsAuthenticated(false);
                setUserData(null);
                setIsLoading(false);
                return;
            }

            const data = await response.json();
            console.log('validateSession - User data:', data);

            if (data.error || !data.datosUsuario) {
                console.log('validateSession - Error or no user data, sesión inválida');
                localStorage.setItem('Login', 'false');
                setIsAuthenticated(false);
                setUserData(null);
            } else {
                console.log('validateSession - Session valid, user:', data.datosUsuario.usuario_id);
                setIsAuthenticated(true);
                setUserData(data.datosUsuario);
            }
        } catch (error) {
            console.error('Error validating session:', error);
            // Si hay error de conexión pero localStorage dice Login: true, mantener autenticado
            const localStorageLogin = localStorage.getItem('Login');
            if (localStorageLogin === 'true') {
                console.log('validateSession - Error de conexión pero localStorage dice true, manteniendo autenticado');
                setIsAuthenticated(true);
            } else {
                localStorage.setItem('Login', 'false');
                setIsAuthenticated(false);
                setUserData(null);
            }
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Validar sesión al cargar el componente
    useEffect(() => {
        console.log('AuthProvider - Initial validation');
        validateSession();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Validación periódica cada 30 minutos
    useEffect(() => {
        const intervalId = setInterval(() => {
            console.log('AuthProvider - Periodic session validation (30 min)');
            validateSession();
        }, 30 * 60 * 1000); // 30 minutos

        return () => clearInterval(intervalId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Validar sesión cuando el usuario regresa a la pestaña
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (!document.hidden) {
                console.log('AuthProvider - Page became visible, validating session');
                validateSession();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Escuchar cambios en localStorage
    useEffect(() => {
        const handleStorageChange = (e) => {
            console.log('Storage event detected:', e.key);
            if (e.key === 'Login') {
                console.log('Login changed in localStorage, validating...');
                validateSession();
            }
        };

        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, [validateSession]);

    // Función para hacer login
    const login = useCallback(async (email, password) => {
        try {
            console.log('login - Starting login for:', email);
            const response = await fetch(Constantes.RUTA_API + '/usuarios/login', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ email, pass: password }),
            });

            const data = await response.json();
            console.log('login - Response:', data);

            if (data.error || data.login === false) {
                console.log('login - Failed');
                localStorage.setItem('Login', 'false');
                setIsAuthenticated(false);
                setUserData(null);
                setIsLoading(false);
                return { success: false, message: data.message };
            } else {
                console.log('login - Success, setting authenticated to true');
                // Actualizar estado inmediatamente
                localStorage.setItem('Login', 'true');
                setIsAuthenticated(true);
                setIsLoading(false);
                
                // Luego validar para obtener datos del usuario
                console.log('login - Validating session...');
                await validateSession();
                
                return { success: true, message: 'Login exitoso' };
            }
        } catch (error) {
            console.error('login - Error:', error);
            localStorage.setItem('Login', 'false');
            setIsAuthenticated(false);
            setUserData(null);
            setIsLoading(false);
            return { success: false, message: 'Error al iniciar sesión' };
        }
    }, [validateSession]);

    const logout = useCallback(async () => {
        console.log('logout - Starting logout');
        try {
            await fetch(Constantes.RUTA_API + '/usuarios/logout', {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json'
                }
            }).catch(() => {
                console.log('logout - Backend logout failed, continuing...');
            });
        } catch (error) {
            console.error('logout - Error:', error);
        } finally {
            console.log('logout - Clearing auth state');
            localStorage.setItem('Login', 'false');
            localStorage.clear();
            sessionStorage.clear();
            setUserData(null);
            setIsAuthenticated(false);
            setIsLoading(false);
        }
    }, []);

    console.log('AuthProvider - Rendering with isAuthenticated:', isAuthenticated, 'isLoading:', isLoading);

    return (
        <AuthContext.Provider
            value={{
                isAuthenticated,
                isLoading,
                userData,
                login,
                logout,
                validateSession
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};
